// LogoutHeldDataDialog — loud confirmation shown when a surveyor signs out
// while the device is still holding un-uploaded capture work, OR while we
// could not verify that it isn't (audit-2026-06-22 C4 / #696).
//
// Logout clears the local SQLite mirror for shared-device PII hygiene, but
// that clear is destructive to two kinds of count that live ONLY on the
// device: entries still queued in `ps_crud` (writes that never reached the
// server) and rows quarantined in `orphaned_capture_items` (counts the
// connector held because they could not sync). Both are contractual asset
// counts. Confirming sign-out runs the H8/H9 preserve-before-clear path
// (#696/#663): the auth-provider first ships the device-held quarantine to the
// durable server recovery store, audits the outcome, THEN clears — so the
// confirm is never a silent wipe. It is still destructive (anything that can't
// be backed up — a still-uploading queued write, or a count that couldn't ship
// offline — is permanently deleted), so — mirroring the ADR-015 room-delete
// loud confirm — the surveyor must acknowledge that explicitly. When the
// held-load probe can't be read (DB locked/initializing, or it timed out), we
// fail CLOSED and show the same dialog in an "unverified" variant rather than
// risk a silent wipe.
//
// Cancel / Esc / overlay close the dialog WITHOUT signing out (the safe
// default — stay signed in and finish syncing). Only the destructive button
// fires `onConfirm`, which proceeds with the clear + sign-out.
//
// Layered on the shared `<Dialog>` primitive (focus trap, Esc-to-close,
// return-focus-to-invoker). `dismissOnOverlayClick={false}` keeps an
// accidental backdrop tap from looking like either choice.

import { type ReactElement } from 'react'

import {
  heldCaptureTotal,
  heldChangeNoun,
  type HeldCaptureLoad,
} from '../hooks/powersync/logout-cleanup'
import { Button } from '../ui/button'
import { Dialog } from '../ui/dialog'

export interface LogoutHeldDataDialogProps {
  /** The held-load probe result, or null when the dialog is closed. */
  load: HeldCaptureLoad | null
  /** Called when the user dismisses without signing out (Cancel/Esc). */
  onCancel: () => void
  /** Called when the user confirms sign-out, discarding the held work. */
  onConfirm: () => void
}

export function LogoutHeldDataDialog({
  load,
  onCancel,
  onConfirm,
}: LogoutHeldDataDialogProps): ReactElement {
  const indeterminate = load !== null && load.status === 'indeterminate'
  // Total at-risk work, from the shared `heldCaptureTotal` definition so this
  // dialog and the capture banner can never put a different NUMBER or NOUN on
  // the same device state (audit PR #732 / #13). They intentionally differ on
  // ONE thing — the indeterminate read: this destructive logout path fails
  // CLOSED (warns, below), whereas the ambient banner HIDES (never a false
  // zero). `null` (indeterminate / closed) shows as 0 here — the indeterminate
  // variant below does not surface the number.
  // We speak of "unsynced changes", not "counts": the quarantine dimension is
  // always captured counts, but the ps_crud queue dimension can also hold
  // non-count writes (room renames, session completes), so "changes" is the
  // accurate umbrella — and it still maps to the device's sync indicator the
  // surveyor watches.
  const total = load !== null ? (heldCaptureTotal(load) ?? 0) : 0
  const noun = heldChangeNoun(total)
  const verb = total === 1 ? "hasn't" : "haven't"
  const pronoun = total === 1 ? 'it' : 'them'

  const title = indeterminate
    ? 'Sign out with unsynced work on this device?'
    : `Sign out with ${total} unsynced ${noun}?`
  // Truthful about the H8/H9 fix (#696/#663): signing out now first backs up the
  // device-held counts it CAN to the durable recovery store, then clears the
  // device — so the confirm is no longer a silent wipe. It is still destructive:
  // anything that can't be backed up (a still-uploading queued write, or a
  // quarantined count we couldn't ship — e.g. offline) is permanently deleted.
  // We therefore neither claim "held safely / nothing lost" (the H9 false
  // reassurance) nor the now-false "can't be recovered".
  const description = indeterminate
    ? "We couldn't check whether this device still has counts that haven't reached the server. Signing out backs up what it can to the recovery store first, then clears this device — anything that can't be backed up is permanently deleted."
    : `${total} ${noun} ${verb} reached the server yet. Signing out backs up what it can to the recovery store, then removes ${pronoun} from this device — anything that can't be backed up is permanently deleted.`
  const confirmLabel = indeterminate
    ? 'Sign out and discard any unsynced work'
    : `Sign out and discard ${total} unsynced ${noun}`

  return (
    <Dialog
      open={load !== null}
      onClose={onCancel}
      title={title}
      description={description}
      dismissOnOverlayClick={false}
    >
      <div className="flex flex-col gap-3" data-testid="logout-held-data-dialog">
        <p className="text-sm text-gray-700" data-testid="logout-held-warning">
          Stay signed in and keep this device online so it can finish saving your
          work — don&apos;t sign out until the held-work indicator clears. Some
          held counts are recovered separately and won&apos;t re-send just by
          waiting.
        </p>
        <div className="mt-2 flex justify-end gap-2">
          <Button
            variant="ghost"
            onClick={onCancel}
            aria-label="Stay signed in and keep your unsynced work"
          >
            Stay signed in
          </Button>
          <Button
            variant="danger"
            onClick={onConfirm}
            aria-label={confirmLabel}
            data-testid="confirm-logout-discard-btn"
          >
            Sign out &amp; discard
          </Button>
        </div>
      </div>
    </Dialog>
  )
}
